import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input } from '@angular/core';
import {
  asyncScheduler,
  BehaviorSubject,
  catchError,
  finalize,
  Observable,
  observeOn,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import {
  AggregationsModel,
  AggregationsType,
  AggregationType,
  ChartsAggregationType,
} from 'src/app/models/api/aggregations.model';
import { PageWithFiltersEnum } from 'src/app/models/filters/pageWithFilters.enum';
import { ISelectedFilters } from 'src/app/models/filters/selectedFilters.interface';
import { FiltersService } from 'src/app/services/filters/filters.service';
import { AggregationsHttpServiceService } from 'src/app/services/httpServices/aggregationsHttpService/aggregations-http-service.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent {
  @Input() public aggregationsType: keyof typeof AggregationsType;

  public isLoading$ = new BehaviorSubject(false);
  public isNewZenityAccount$ = new BehaviorSubject(false);

  public selectedFilter$ = this._filtersService.getFilters(PageWithFiltersEnum.Dashboards)
    .onUpdate$ as BehaviorSubject<ISelectedFilters>;

  public aggregationsModel$ = this.selectedFilter$.pipe(
    observeOn(asyncScheduler),
    tap(() => {
      this.isLoading$.next(true);
      this.isNewZenityAccount$.next(false);
    }),
    switchMap((filters) =>
      this._aggregationsService.getAggregations(AggregationsType[this.aggregationsType], filters).pipe(
        switchMap((response) => this._fillMissingAggregations(response)),
        finalize(() => this.isLoading$.next(false)),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            this.isNewZenityAccount$.next(true);
          }
          const emptyAggregationsModel = {
            zenityAccountId: '',
            aggregationsData: {},
          } as AggregationsModel;
          return this._fillMissingAggregations(emptyAggregationsModel);
        })
      )
    ),
    shareReplay(1)
  );

  constructor(
    private readonly _aggregationsService: AggregationsHttpServiceService,
    private readonly _filtersService: FiltersService
  ) {}

  private _fillMissingAggregations(response: AggregationsModel): Observable<AggregationsModel> {
    const supportedKeys = Object.keys(AggregationType).concat(Object.keys(ChartsAggregationType));
    for (const key in supportedKeys) {
      if (supportedKeys[key] in response.aggregationsData) continue;
      else {
        const data = [
          ChartsAggregationType.topRiskyUsers.toString(),
          ChartsAggregationType.topRiskyEnvironments.toString(),
          AggregationType.topBuilders.toString(),
          ChartsAggregationType.owaspBySeverities.toString(),
          ChartsAggregationType.owaspLlmBySeverities.toString(),
          ChartsAggregationType.mitreBySeverities.toString(),
        ].includes(supportedKeys[key])
          ? []
          : {};

        response.aggregationsData[
          supportedKeys[key] as keyof typeof AggregationType | keyof typeof ChartsAggregationType
        ] = {
          data: data,
        };
      }
    }
    return of(response);
  }
}
